const API_BASE_URL =
  process.env.REACT_APP_SC_API_BASE_URL || "http://localhost:8080";

/**
 * Uploads a file to S3 via a presigned URL.
 * 1. Requests a presigned PUT URL from the backend
 * 2. PUTs the file directly to S3
 * 3. Returns the fileKey (UUID-prefixed filename) for storage
 *
 * @param {File|Blob} file - The file or blob to upload
 * @param {string} fileName - Original filename (used for key generation)
 * @param {string} token - JWT auth token
 * @param {string} [contentType] - Explicit content type (use for blobs where .type may be empty)
 * @returns {Promise<string>} The fileKey to store/reference
 */
export async function uploadFile(file, fileName, token, contentType) {
  const resolvedContentType = contentType || file.type;
  if (!resolvedContentType) {
    throw new Error("Cannot determine file content type");
  }

  // Step 1: Get presigned URL from backend
  const presignedResponse = await fetch(
    `${API_BASE_URL}/api/files/presigned-upload`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName: fileName,
        contentType: resolvedContentType,
        fileSize: file.size,
      }),
    }
  );

  if (!presignedResponse.ok) {
    const errorText = await presignedResponse.text();
    throw new Error(`Failed to get presigned URL: ${errorText}`);
  }

  const { presignedUrl, fileKey } = await presignedResponse.json();

  // Step 2: PUT file directly to S3 (Content-Type must match exactly what was signed)
  const uploadResponse = await fetch(presignedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": resolvedContentType,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file to S3: ${uploadResponse.status}`);
  }

  // Step 3: Return the fileKey (UUID-prefixed filename)
  return fileKey;
}
